//= require n2words/dist/n2words.js
import { Controller } from '@hotwired/stimulus';

const OFFER_CODE_DEBOUNCE_MS = 1500;

export default class SubscriptionConfirmationController extends Controller {
  static targets = [
    'form',
    'amountInput',
    'amountInWordsInput',
    'inProgressIcon',
    'cancelIcon',
    'checkIcon',
    'confirmButton',
    'amountInWordsTips',
    'offerCodeInput',
    'offerCodeValidating',
    'offerCodeMessage',
    'offerCodeBonus',
    'offerCodeBonusAmount',
    'profileSelect',
  ];

  static values = {amountInWords: String}

  #offerCodeDebounceTimer = null;

  connect() {
    if (this.hasAmountInWordsInputTarget && this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true;
    }
  }

  limitInputLength(event) {
    const AMOUNT_LENGTH_LIMIT = 9;
    if (event.target.value.length > AMOUNT_LENGTH_LIMIT) {
      event.target.value = event.target.value.slice(0, AMOUNT_LENGTH_LIMIT);
    }
  }

  onAmountKeyup() {
    this.syncAmountInWords();
    this.validateAmountInWords();
    this.#scheduleOfferCodeValidation();
  }

  populateFromSimulator(event) {
    if (!this.hasAmountInputTarget) return;

    this.amountInputTarget.value = event.detail.amount;
    this.onAmountKeyup();
  }

  syncAmountInWords() {
    const amount = parseInt(this.amountInputTarget.value, 10) || 0;

    if (amount === 0) {
      this.amountInWordsValue = '';
      return;
    }

    this.amountInWordsInputTarget.removeAttribute('disabled');
    this.amountInWordsValue = n2words(amount, { lang: getCurrentLocale() });
  }

  amountInWordsValueChanged(currentValue) {
    if (this.hasAmountInWordsTipsTarget) {
      this.amountInWordsTipsTarget.innerText = currentValue;
    }
  }

  validateAmountInWords() {
    if (!this.hasAmountInWordsInputTarget) return;

    const amountTyped = this.amountInWordsInputTarget.value;

    if (amountTyped.length <= 2) {
      [this.inProgressIconTarget, this.cancelIconTarget, this.checkIconTarget].forEach(
        el => el.classList.add('d-none')
      );

      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
      return;
    }

    const formatted = this.formatAmountText(this.amountInWordsValue);
    const typed = this.formatAmountText(amountTyped);

    if (formatted === typed) {
      this.inProgressIconTarget.classList.add('d-none');
      this.cancelIconTarget.classList.add('d-none');
      this.checkIconTarget.classList.remove('d-none');
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = false;
    } else if (formatted.substring(0, typed.length) === typed) {
      this.inProgressIconTarget.classList.remove('d-none');
      this.cancelIconTarget.classList.add('d-none');
      this.checkIconTarget.classList.add('d-none');
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
    } else {
      this.inProgressIconTarget.classList.add('d-none');
      this.cancelIconTarget.classList.remove('d-none');
      this.checkIconTarget.classList.add('d-none');
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
    }
  }

  checkAndConfirm(event) {
    if (!this.hasAmountInputTarget) return;
    if (!this.hasFormTarget) return;

    if (!this.formTarget.reportValidity()) return;

    if (this.hasConfirmButtonTarget && this.confirmButtonTarget.dataset.missingQa) {
      new bootstrap.Modal(document.getElementById('qaRequiredModal')).show();
      return;
    }

    const lossesCapacity = this.amountInputTarget.dataset.lossesCapacity;
    const amount = parseInt(this.amountInputTarget.value, 10);

    if (isNaN(amount)) return;

    this.populateConfirmationModal(amount);

    if (lossesCapacity && amount > parseInt(lossesCapacity, 10)) {
      new bootstrap.Modal(document.getElementById('subscriptionWarningModal')).show();
    } else {
      new bootstrap.Modal(document.getElementById('subscriptionConfirmationModal')).show();
    }
  }

  handleWarningAccepted() {
    bootstrap.Modal.getInstance(document.getElementById('subscriptionWarningModal')).hide();
    new bootstrap.Modal(document.getElementById('subscriptionConfirmationModal')).show();
  }

  handleConfirmSubmit() {
    const confirmationModal = document.getElementById('subscriptionConfirmationModal');
    bootstrap.Modal.getInstance(confirmationModal).hide();
    if (confirmationModal.pendingForm) {
      confirmationModal.pendingForm.submit();
    }
  }

  populateConfirmationModal(amount) {
    const confirmationModal = document.getElementById('subscriptionConfirmationModal');
    const selectedCurrency = confirmationModal.dataset.projectCurrency;
    const formatter = new Intl.NumberFormat(getCurrentLocale(), { style: 'currency', currency: selectedCurrency });

    const amountEl = confirmationModal.querySelector('#amount');
    if (amountEl) {
      amountEl.innerText = formatter.format(amount);
    }

    const projectEl = confirmationModal.querySelector('#project') || confirmationModal.querySelector('#project_name');
    if (projectEl) {
      projectEl.innerText = confirmationModal.dataset.projectName;
    }

    const investorEl = confirmationModal.querySelector('#investor_name');
    if (investorEl && this.hasProfileSelectTarget) {
      investorEl.innerText = this.#selectedInvestorName();
    }

    confirmationModal.pendingForm = this.formTarget;
  }

  #selectedInvestorName() {
    const selectedOption = this.profileSelectTarget.selectedOptions[0];

    if (!selectedOption) return '';

    return this.#parseInvestorName(selectedOption.text.trim());
  }

  #parseInvestorName(name) {
    const nameParts = name.split(' - ');

    if (nameParts.length === 2) {
      return nameParts[0];
    }

    if (nameParts.length === 3) {
      const [companyName, fullName] = nameParts;

      return `${companyName} (${fullName})`;
    }

    return name;
  }

  formatAmountText(text) {
    return text.normalize('NFD').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  disconnect() {
    clearTimeout(this.#offerCodeDebounceTimer);
  }

  onOfferCodeInput() {
    this.#scheduleOfferCodeValidation();
  }

  #scheduleOfferCodeValidation() {
    if (!this.hasOfferCodeInputTarget) return;

    const code = this.offerCodeInputTarget.value.trim();

    if (!code) {
      this.#resetOfferCodeState();
      return;
    }

    const amount = this.#currentAmount();

    if (!amount || amount <= 0) {
      this.#resetOfferCodeState();
      return;
    }

    this.#showOfferCodeValidating();
    clearTimeout(this.#offerCodeDebounceTimer);
    this.#offerCodeDebounceTimer = setTimeout(() => {
      this.#performOfferCodeValidation(code, amount);
    }, OFFER_CODE_DEBOUNCE_MS);
  }

  #currentAmount() {
    if (!this.hasAmountInputTarget) return 0;
    return parseInt(this.amountInputTarget.value, 10) || 0;
  }

  async #performOfferCodeValidation(code, amount) {
    const validationUrl = this.offerCodeInputTarget.dataset.validationUrl;
    const projectId = this.offerCodeInputTarget.dataset.projectId;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    const formData = new URLSearchParams();
    formData.append('offer_code_validation[code]', code);
    formData.append('offer_code_validation[project_id]', projectId);
    formData.append('offer_code_validation[amount]', amount);

    try {
      const response = await fetch(validationUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success === false) {
        const overrideMessage = this.#offerCodeErrorOverride(data.error_code);
        this.#showOfferCodeError(overrideMessage || data.errors[0]);
      } else {
        this.#showOfferCodeSuccess(data.message, data.bonus || 0);
      }
    } catch (error) {
      console.error('Error validating offer code:', error);
    }
  }

  #showOfferCodeValidating() {
    if (this.hasOfferCodeMessageTarget && this.hasOfferCodeValidatingTarget) {
      this.offerCodeMessageTarget.textContent = this.offerCodeValidatingTarget.textContent.trim();
      this.offerCodeMessageTarget.className = 'text-secondary fs-xs fst-italic';
    }

    this.#hideOfferCodeBonus();
    this.#disableConfirmButton();
  }

  #showOfferCodeSuccess(message, bonus) {
    this.offerCodeInputTarget.classList.remove('is-invalid');
    this.offerCodeInputTarget.classList.add('is-valid');

    if (this.hasOfferCodeMessageTarget) {
      this.offerCodeMessageTarget.textContent = message;
      this.offerCodeMessageTarget.className = 'text-success-800 fs-xs';
    }

    this.#showOfferCodeBonus(bonus);
    this.#enableConfirmButton();
  }

  #showOfferCodeError(errorMessage) {
    this.offerCodeInputTarget.classList.remove('is-valid');
    this.offerCodeInputTarget.classList.add('is-invalid');

    if (this.hasOfferCodeMessageTarget) {
      this.offerCodeMessageTarget.textContent = errorMessage;
      this.offerCodeMessageTarget.className = 'text-danger-800 fs-xs';
    }

    this.#hideOfferCodeBonus();
    this.#disableConfirmButton();
  }

  #resetOfferCodeState() {
    if (!this.hasOfferCodeInputTarget) return;

    clearTimeout(this.#offerCodeDebounceTimer);
    this.offerCodeInputTarget.classList.remove('is-valid', 'is-invalid');

    if (this.hasOfferCodeMessageTarget) {
      this.offerCodeMessageTarget.textContent = '';
      this.offerCodeMessageTarget.className = 'd-none';
    }

    this.#hideOfferCodeBonus();
    this.#enableConfirmButton();
  }

  #showOfferCodeBonus(bonus) {
    if (!this.hasOfferCodeBonusTarget) return;

    this.offerCodeBonusTarget.classList.remove('d-none');

    if (this.hasOfferCodeBonusAmountTarget && bonus) {
      this.offerCodeBonusAmountTarget.textContent = ` ${bonus}€`;
    }
  }

  #hideOfferCodeBonus() {
    if (this.hasOfferCodeBonusTarget) {
      this.offerCodeBonusTarget.classList.add('d-none');
    }
  }

  #disableConfirmButton() {
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true;
    }
  }

  #enableConfirmButton() {
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = false;
    }
  }

  #offerCodeErrorOverride(errorCode) {
    if (!this.hasOfferCodeInputTarget) return null;

    if (errorCode === 'not_exist') {
      return this.offerCodeInputTarget.dataset.notExistMessage || null;
    }

    return null;
  }
}
